import { useCallDuration, useSoftphone } from "@dialstack/sdk-react/softphone";
import { MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";

import { selectCallNoteDraft } from "../call-notes.slice";
import { selectLivePrivacy } from "../hidden-calls.slice";
import BarFactLine from "./bar-fact-line.component";
import { barFacts } from "./bar-facts.util";
import {
  callBarAvatarSx,
  callBarFoldButtonSx,
  callBarNameSx,
  callBarSx
} from "./dialstack-softphone-widget.styles";
import SoftphoneCallControls, {
  callBarState
} from "./softphone-call-controls.component";
import useCallIdentity from "./use-call-identity.hook";
import type { InboundCallMatch } from "./use-inbound-call-pops.hook";
import useTransferStatus from "./use-transfer-status.hook";

type SoftphoneCall = ReturnType<typeof useSoftphone>["calls"][number];

// The folded size of the call pop (ENG-2226): one 56px pill per live call in
// the phone button's corner. Left half, who and what state; right half, the
// two controls that matter in that state and the chevron that unfolds the
// pop. The second line steps through the call's facts in words.
export default function SoftphoneCallBar({
  call,
  match,
  onUnfold
}: {
  call: SoftphoneCall;
  match: InboundCallMatch | null;
  // Absent on a bar that cannot unfold: a live call sitting under the pop
  // of a second call that is ringing.
  onUnfold?: () => void;
}) {
  const navigate = useNavigate();
  const state = callBarState(call);
  const duration = useCallDuration(call);
  const identity = useCallIdentity(call, match);
  const transferStatus = useTransferStatus();
  const noteDraft = useAppSelector((state) =>
    selectCallNoteDraft(state, match?.backendCallId ?? null)
  );
  const livePrivacy = useAppSelector((state) =>
    selectLivePrivacy(state, call.id)
  );
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const facts = barFacts({
    state,
    duration: state === "ringing" ? null : duration,
    patient: identity.patient,
    transferredBy: identity.transferredBy,
    transferStatus: transferStatus(call.id),
    caller: identity.caller,
    sharedPatientCount: identity.sharedPatientCount,
    noteSaved: noteDraft?.status === "saved" && noteDraft.text.trim() !== "",
    privateCall: livePrivacy?.status === "on"
  });

  const handleNameClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (identity.href) navigate(identity.href);
  };

  return (
    <Box
      className="suno-phone-drag-handle"
      data-testid="softphone-call-bar"
      data-call-state={state}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      sx={callBarSx}
    >
      <Avatar
        src={identity.photo || undefined}
        sx={[
          callBarAvatarSx,
          state === "ringing" && {
            animation: "suno-bar-ring 1.6s ease-out infinite"
          }
        ]}
      >
        {identity.initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {identity.href ? (
          <Link
            href={identity.href}
            onClick={handleNameClick}
            underline="hover"
            className="suno-phone-no-drag"
            sx={callBarNameSx}
          >
            {identity.name}
          </Link>
        ) : (
          <Typography component="span" sx={callBarNameSx}>
            {identity.name}
          </Typography>
        )}
        <BarFactLine
          facts={facts}
          paused={hovered || focused}
          tone={
            state === "held" ? "held" : state === "active" ? "live" : "default"
          }
        />
      </Box>
      <SoftphoneCallControls call={call} state={state} />
      {onUnfold && (
        <Tooltip title="Show details">
          <IconButton
            aria-label="Show details"
            className="suno-phone-no-drag"
            size="small"
            onClick={onUnfold}
            sx={callBarFoldButtonSx}
          >
            <ExpandLessIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
